import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

interface CompraDTO {
  compraId: number;
  pacoteId: number;
  pacoteNome: string;
  pacote?: PacoteDTO;
}

interface PacoteDTO {
  id: number;
  titulo: string;
  pontosDeInteresse?: string[];
  pontosImportantes?: string[];
}

interface ArquivoPreview {
  file: File | null;
  previewUrl: string;
  pontoSelecionado?: string | null;
  arquivoId?: number;
}

interface PostagemDTO {
  id: number;
  conteudo: string;
  pacoteCompraId: number;
  arquivos: { id: number; url: string; pontoInteresse?: string }[];
}

interface NovaPostagemProps {
  onPostCreated?: () => void;
  postagemParaEditar?: PostagemDTO; // modo edição
  onPostEdited?: (postagemAtualizada: PostagemDTO) => void;
}

const BASE_URL = process.env.REACT_APP_API_URL;

const NovaPostagem: React.FC<NovaPostagemProps> = ({
  onPostCreated,
  postagemParaEditar,
  onPostEdited,
}) => {
  const [conteudo, setConteudo] = useState("");
  const [pacoteCompraId, setPacoteCompraId] = useState<number | "">("");
  const [compras, setCompras] = useState<CompraDTO[]>([]);
  const [arquivos, setArquivos] = useState<ArquivoPreview[]>([]);
  const [carregando, setCarregando] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 🔹 Buscar pacotes comprados do usuário e detalhes do pacote
  useEffect(() => {
    const fetchCompras = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get<CompraDTO[]>(`${BASE_URL}/pacotes/compras/minhas`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const comprasComPacote = await Promise.all(
          res.data.map(async (c) => {
            try {
              const pacoteRes = await axios.get<PacoteDTO>(`${BASE_URL}/pacotes/${c.pacoteId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
              });
              return { ...c, pacote: pacoteRes.data };
            } catch (err) {
              console.error("Erro ao buscar pacote", c.pacoteId, err);
              return c;
            }
          })
        );

        setCompras(comprasComPacote);
      } catch (err) {
        console.error("Erro ao carregar compras", err);
      }
    };
    fetchCompras();
  }, []);

  // Preencher o form se estiver editando
  useEffect(() => {
    if (postagemParaEditar) {
      setConteudo(postagemParaEditar.conteudo || "");
      setPacoteCompraId(postagemParaEditar.pacoteCompraId || "");
      setArquivos(
        (postagemParaEditar.arquivos || []).map((a) => ({
          file: null,
          previewUrl: `${BASE_URL}${a.url}`,
          pontoSelecionado: a.pontoInteresse || null,
          arquivoId: a.id, // id do arquivo já existente
        }))
      );
    } else {
      limparFormulario();
    }
  }, [postagemParaEditar]);

  const limparFormulario = () => {
    setConteudo("");
    setPacoteCompraId("");
    setArquivos([]);
  };

  // 🔹 Combinar pontos do pacote selecionado
  const getPontosParaSelect = (): string[] => {
    if (!pacoteCompraId) return [];
    const compra = compras.find((c) => c.compraId === pacoteCompraId);
    if (!compra?.pacote) return [];
    const pontos = [...(compra.pacote.pontosDeInteresse || []), ...(compra.pacote.pontosImportantes || [])];
    return Array.from(new Set(pontos));
  };

  // 🔹 Selecionar arquivos (preview antes do upload real)
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const previews = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      pontoSelecionado: null,
    }));
    setArquivos((prev) => [...prev, ...previews]);
  };

  // 🔹 Remover arquivo
  const handleRemoveFile = (previewUrl: string) => {
    setArquivos((prev) => prev.filter((a) => a.previewUrl !== previewUrl));
  };

  // Cancelar edição
  const handleCancelar = () => {
    limparFormulario();
    // Se estiver no modo edição, limpar estado no componente pai
    if (postagemParaEditar && onPostEdited) {
      onPostEdited(postagemParaEditar); // opcional, depende da lógica que você quer
    }
  };

  // Criar ou editar postagem
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conteudo.trim() || !pacoteCompraId) return;

    setCarregando(true);

    try {
      const token = localStorage.getItem("token");
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

      // 🔹 Upload dos novos arquivos (os que têm `file`)
      const novosArquivos: any[] = [];
      for (const arq of arquivos) {
        if (!arq.file) continue; // só faz upload dos novos
        const formData = new FormData();
        formData.append("file", arq.file);

        const res = await axios.post(`${BASE_URL}/uploads`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...authHeader,
          },
        });

        novosArquivos.push({
          arquivoId: res.data.id,
          pontoInteresse: arq.pontoSelecionado || null
        });
      }

      // 🔹 Arquivos já existentes (mantidos)
      const arquivosMantidos = arquivos
        .filter((a) => a.arquivoId && !a.file) // só os já existentes
        .map((a) => ({
          id: a.arquivoId,
          pontoInteresse: a.pontoSelecionado || null
        }));

      // 🔹 Monta payload para criar ou editar
      let payload: any;

      if (postagemParaEditar) {
        // EDIÇÃO
        payload = {
          conteudo,
          pacoteCompraId,
          arquivosMantidos,
          novosArquivos,
        };

        const res = await axios.put(
          `${BASE_URL}/postagens/${postagemParaEditar.id}`,
          payload,
          { headers: authHeader }
        );

        if (onPostEdited) onPostEdited(res.data);
      } else {
        // CRIAÇÃO
        payload = {
          conteudo,
          pacoteCompraId,
          arquivos: [
            ...arquivosMantidos.map((a) => ({
              arquivoId: a.id,
              pontoInteresse: a.pontoInteresse
            })),
            ...novosArquivos,
          ],
        };

        const res = await axios.post(`${BASE_URL}/postagens`, payload, {
          headers: authHeader,
        });

        if (onPostCreated) onPostCreated();
      }

      limparFormulario();
    } catch (err) {
      console.error("Erro ao salvar postagem:", err);
    } finally {
      setCarregando(false);
    }
  };


  return (
    <div className="card mb-3 shadow-sm">
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="alert alert-warning mb-3">
            Lembre-se: só é possível publicar sobre pacotes que você já comprou e concluiu.
            Todos os posts passam por uma análise antes de ficarem visíveis para todos.
          </div>
          {/* Texto */}
          <textarea
            className="form-control mb-3"
            placeholder="No que você está pensando?"
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
            rows={3}
          />

          {/* Selecionar pacote */}
          <select
            className="form-control mb-3"
            value={pacoteCompraId}
            onChange={(e) => setPacoteCompraId(Number(e.target.value))}
            required
          >
            <option value="">Selecione um pacote</option>
            {compras.map((c) => (
              <option key={c.compraId} value={c.compraId}>
                {c.pacoteNome}
              </option>
            ))}
          </select>

          {/* Preview de arquivos */}
          {arquivos.length > 0 && (
            <div className="mb-3 d-flex flex-wrap gap-3">
              {arquivos.map((a, idx) => (
                <div
                  key={a.previewUrl}
                  className="p-2 border rounded d-flex flex-column align-items-center"
                  style={{ width: "220px", background: "#f9f9f9" }}
                >
                  <video
                    src={a.previewUrl}
                    style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: 6 }}
                    muted
                    controls
                    onError={(e) => {
                      // Se não carregar como vídeo, substitui por imagem
                      const target = e.currentTarget as HTMLVideoElement;
                      const parent = target.parentElement;
                      if (parent) {
                        const img = document.createElement("img");
                        img.src = a.previewUrl;
                        img.style.width = "100%";
                        img.style.height = "120px";
                        img.style.objectFit = "cover";
                        img.style.borderRadius = "6px";
                        target.replaceWith(img);
                      }
                    }}
                  />

                  {getPontosParaSelect().length > 0 && (
                    <select
                      className="form-control mt-2"
                      value={a.pontoSelecionado || ""}
                      onChange={(e) =>
                        setArquivos((prev) =>
                          prev.map((arq, i) =>
                            i === idx ? { ...arq, pontoSelecionado: e.target.value } : arq
                          )
                        )
                      }
                    >
                      <option value="">Escolha um ponto (opcional)</option>
                      {getPontosParaSelect().map((ponto) => (
                        <option key={ponto} value={ponto}>
                          {ponto}
                        </option>
                      ))}
                    </select>
                  )}

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger mt-2 w-100"
                    onClick={() => handleRemoveFile(a.previewUrl)}
                  >
                    ❌ Remover
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Adicionar arquivos */}
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              📷📹 Adicionar arquivos
            </button>
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </div>

          {/* Botões */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={carregando}>
              {carregando ? "Salvando..." : postagemParaEditar ? "Salvar alterações" : "Publicar"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancelar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaPostagem;